import { examQueryKeys } from "@/api/query-keys/exams"
import {
  EXAM_STORAGE_KEY,
  useExamFilters,
} from "@/hooks/filters/useExamFilters"
import ScreenWrapper from "@/components/ScreenWrapper"
import { checkPermissions } from "@/utils/checkPermissions"
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router"
import { z } from "zod"
import {
  TABLE_PAGE_SIZE,
  usePaginatedTableWithUrlState,
  createTableSearchSchema,
  DataTable,
  selectColumn,
  createSortableColumn,
  createActionsColumn,
  useExportMutation,
  Exam,
  Button,
  createCustomColumn,
  getExamLabel,
  examTypeOptionsOwner,
  examTypeOptionsCategory,
  examTypeOptionsSkill,
  examTypeOptionsLevel,
  MultiLevelDropdownMenu,
} from "toptaal-shared"
import type { ColumnDef } from "@tanstack/react-table"
import { TableHeaderActions } from "@/components/data-table/TableHeaderActions"
import EditExamModal from "@/components/modals/exams/EditExamModal"
import DeleteExamModal from "@/components/modals/exams/DeleteExamModal"
import ArchiveExamModal from "@/components/modals/exams/ArchiveExamModal"
import UnarchiveExamModal from "@/components/modals/exams/UnarchiveExamModal"
import { useState } from "react"
import { ArchiveBoxIcon } from "@heroicons/react/24/outline"

const ALL_COLUMN_IDS = ["id", "title", "owner", "category", "skill", "level"]

export const Route = createFileRoute("/beheer/toetsen/")({
  component: RouteComponent,
  beforeLoad: () => checkPermissions("read", "exams"),
  validateSearch: createTableSearchSchema().extend({
    archived: z.boolean().optional().catch(false),
  }),
})

function RouteComponent() {
  const [isEditOpen, setIsEditOpen] = useState(false)
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [isArchiveOpen, setIsArchiveOpen] = useState(false)
  const [isUnarchiveOpen, setIsUnarchiveOpen] = useState(false)
  const [activeRow, setActiveRow] = useState<Exam | null>(null)

  const searchParams = useSearch({ from: "/beheer/toetsen/" })
  const navigate = useNavigate({ from: "/beheer/toetsen/" })
  const { archived } = searchParams

  const { filterItems, activeFilters, queryFn } = useExamFilters({
    onPageReset: () =>
      navigate({ search: (prev) => ({ ...prev, page: 1 }), replace: true }),
    archived,
  })

  const {
    isFetching,
    data,
    search,
    setSearch,
    sortField,
    setSortField,
    sortDirection,
    setSortDirection,
    pagination,
    columnVisibility,
    setColumnVisibility,
    mergedFilters,
    selectedColumns,
    rowSelection,
    setRowSelection,
    selectedIds,
    queryKeyMerged,
  } = usePaginatedTableWithUrlState({
    queryKey: [...examQueryKeys.all, activeFilters, { archived }],
    queryFn,
    pageSize: TABLE_PAGE_SIZE,
    searchParams,
    navigate,
    storageKey: EXAM_STORAGE_KEY,
    allColumnIds: ALL_COLUMN_IDS,
  })

  const columns: ColumnDef<Exam>[] = [
    selectColumn(),
    createSortableColumn({ accessorKey: "id", name: "ID" }),
    createSortableColumn({ accessorKey: "title", name: "Titel" }),
    createCustomColumn({
      id: "owner",
      name: "Eigenaar",
      accessorKey: "owner",
      cell: ({ row }) => getExamLabel(examTypeOptionsOwner, row.original.owner),
    }),
    createCustomColumn({
      id: "category",
      name: "Categorie",
      accessorKey: "category",
      cell: ({ row }) =>
        getExamLabel(examTypeOptionsCategory, row.original.category),
    }),
    createCustomColumn({
      id: "skill",
      name: "Vaardigheid",
      accessorKey: "skill",
      cell: ({ row }) => getExamLabel(examTypeOptionsSkill, row.original.skill),
    }),
    createCustomColumn({
      id: "level",
      name: "Niveau",
      accessorKey: "level",
      cell: ({ row }) => getExamLabel(examTypeOptionsLevel, row.original.level),
    }),
    createActionsColumn([
      {
        label: "Toets wijzigen",
        onClick: (e, exam) => {
          e.stopPropagation()
          setActiveRow(exam)
          setIsEditOpen(true)
        },
      },
      {
        label: "Toets verwijderen",
        onClick: (e, exam) => {
          e.stopPropagation()
          setActiveRow(exam)
          setIsDeleteOpen(true)
        },
      },
      ...(archived
        ? [
            {
              label: "Toets herstellen",
              onClick: (e: React.MouseEvent, exam: Exam) => {
                e.stopPropagation()
                setActiveRow(exam)
                setIsUnarchiveOpen(true)
              },
            },
          ]
        : [
            {
              label: "Toets archiveren",
              onClick: (e: React.MouseEvent, exam: Exam) => {
                e.stopPropagation()
                setActiveRow(exam)
                setIsArchiveOpen(true)
              },
            },
          ]),
    ]),
  ]

  const handleToggleArchived = () => {
    navigate({
      search: (prev) => ({
        ...prev,
        archived: !archived,
        page: 1,
      }),
    })
  }

  const { mutate: exportExam } = useExportMutation({
    exportApiPrefix: "exams",
    filters: mergedFilters,
    selectedColumns,
    selectedIds,
    activeFilters,
  })

  return (
    <ScreenWrapper showManagementMenu>
      <DataTable
        title={archived ? "Toetsen archief" : "Toetsen"}
        data={data?.data.exams || []}
        isLoading={isFetching}
        pagination={pagination}
        sortDirection={sortDirection}
        sortField={sortField}
        search={search}
        setSearch={setSearch}
        setSortField={setSortField}
        setSortDirection={setSortDirection}
        columnVisibility={columnVisibility}
        setColumnVisibility={setColumnVisibility}
        rowSelection={rowSelection}
        setRowSelection={setRowSelection}
        columns={columns}
        headerLeftActions={
          <>
            <MultiLevelDropdownMenu items={filterItems} />
            <Button
              onClick={handleToggleArchived}
              variant={archived ? "primary" : "secondary"}
              icon={<ArchiveBoxIcon className="size-5" />}
            >
              Archief
            </Button>
          </>
        }
        headerRightActions={
          <>
            <TableHeaderActions
              exportAction={{
                onClick: exportExam,
              }}
            />

            <TableHeaderActions
              createAction={{
                label: "Nieuwe toets",
                to: "/beheer/toetsen/aanmaken",
                entity: "exams",
                permissions: "create",
              }}
            />
          </>
        }
      />

      <EditExamModal
        isEditOpen={isEditOpen}
        setIsEditOpen={setIsEditOpen}
        activeRow={activeRow}
        closeDialog={() => setIsEditOpen(false)}
      />

      {isDeleteOpen && activeRow && (
        <DeleteExamModal
          isOpen={isDeleteOpen}
          setIsOpen={setIsDeleteOpen}
          exam={activeRow}
          queryKey={queryKeyMerged}
        />
      )}

      {isArchiveOpen && activeRow && (
        <ArchiveExamModal
          isOpen={isArchiveOpen}
          setIsOpen={setIsArchiveOpen}
          exam={activeRow}
        />
      )}

      {isUnarchiveOpen && activeRow && (
        <UnarchiveExamModal
          isOpen={isUnarchiveOpen}
          setIsOpen={setIsUnarchiveOpen}
          exam={activeRow}
        />
      )}
    </ScreenWrapper>
  )
}
